import { Controller } from "@hotwired/stimulus"

// 無限ループのロゴマーキー。
// トラックにはERB側で同一セットをrepeat回並べておき、JSは1セット分(ピッチ)進むたびに
// 剰余で巻き戻すだけ。DOMは変更しないため何周しても空にならない。
//
// data-controller="marquee"
// data-marquee-speed-value="60"   // px/sec
// data-marquee-phase-value="0.5"  // 0.0..1.0 (開始位相。1セット分の中でのオフセット)
// data-marquee-repeat-value="3"   // トラック内のセット数(2以上。1セット幅+画面幅を覆える数にする)
export default class extends Controller {
  static values = {
    speed: { type: Number, default: 60 },
    phase: { type: Number, default: 0 },
    repeat: { type: Number, default: 2 }
  }

  connect() {
    this.track = this.element.querySelector(".marquee__track")
    if (!this.track) return

    // CSSアニメーションを無効化してJS駆動に切り替え
    this.element.classList.add("marquee--js")
    this.track.style.animation = "none"
    this.track.style.transform = "translate3d(0,0,0)"

    this.offset = 0
    this.pitch = 0
    this._started = false
    this._lastTs = undefined

    this._resizeObserver = new ResizeObserver(() => this._measure())
    this._resizeObserver.observe(this.track)
    this._resizeObserver.observe(this.element)

    // 画像の読み込み完了を待ってから開始(左端スタート安定化)
    const imgs = Array.from(this.track.querySelectorAll("img"))
    const notReady = imgs.filter(img => !img.complete)
    if (notReady.length > 0) {
      let pending = notReady.length
      const onLoad = () => {
        pending -= 1
        if (pending <= 0) this._initStart()
      }
      notReady.forEach(img => {
        img.addEventListener("load", onLoad, { once: true })
        img.addEventListener("error", onLoad, { once: true })
      })
      // セーフティ: 1秒で強制開始(CDN遅延など)
      this._fallbackTimer = setTimeout(() => this._initStart(), 1000)
    } else {
      this._initStart()
    }
  }

  disconnect() {
    if (this._raf) cancelAnimationFrame(this._raf)
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this._fallbackTimer) clearTimeout(this._fallbackTimer)
  }

  _measure = () => {
    const style = window.getComputedStyle(this.track)
    const gap = parseFloat(style.gap) || parseFloat(style.columnGap) || 0
    const repeat = Math.max(1, this.repeatValue || 2)
    // セット間の境界にもgapが入るため、実際のピッチは (scrollWidth + gap) / repeat
    this.pitch = (this.track.scrollWidth + gap) / repeat
    this._applyTransform()
  }

  _initStart() {
    if (this._started) return
    this._measure()
    this.offset = -((this.phaseValue % 1) * this.pitch || 0)
    this._lastTs = undefined
    this._applyTransform()
    this._started = true
    this._raf = requestAnimationFrame(this._tick)
  }

  _tick = (ts) => {
    if (this._lastTs == null) this._lastTs = ts
    // タブ非表示からの復帰などで経過時間が跳んでも一気に進めない
    const dt = Math.min((ts - this._lastTs) / 1000, 0.1)
    this._lastTs = ts

    if (this.pitch > 0) {
      this.offset -= this.speedValue * dt
      // 1セット分進んだら剰余で巻き戻す。同一セットが並んでいるため見た目は連続
      if (this.offset <= -this.pitch) {
        this.offset = -((-this.offset) % this.pitch)
      }
      this._applyTransform()
    }

    this._raf = requestAnimationFrame(this._tick)
  }

  _applyTransform() {
    const rounded = Math.round(this.offset * 100) / 100
    this.track.style.transform = `translate3d(${rounded}px,0,0)`
  }
}
